/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Select, Table, Tag } from 'antd';
import { RefSelectProps, SelectProps } from 'antd/lib/select';
import { TableProps } from 'antd/lib/table';
import { Fragment, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';
import { useFolders } from '~src/hooks/api-hooks/useFolders';

// local imports
import { useSecurityGroups } from '~src/hooks/api-hooks/useSecurityGroups';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { CreateFolderPermissionParams } from '~src/services/api';
import { fadeInKeyframes } from '~src/styles/animations';
import { DbFolder, DbFolderPermission, DbSecurityGroup } from '~src/types/db';
import { RIGHTS_LEVEL, SEC_GROUP_PRIORITY } from '~src/types/enums';
import { getParentsIds } from '~src/utils/tree';
import { tabStyleFn, mapSecGroupsToSections } from './tabs.util';
import {
  secGroupColorMap,
  secGroupSectionsConfig,
  arrToTree,
  TableEntry,
} from '~src/utils/securityGroupsConfig';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';

export type AlteredDbFolderPermission = DbFolderPermission & {
  altered?: boolean;
};
export interface ProjectWideTabProps {
  folder?: DbFolder;
  permissionList: Array<{
    folderId: string | null;
    permissions: Array<
      AlteredDbFolderPermission | CreateFolderPermissionParams['permissionData']
    >;
  }>;
  onAddPermission: (
    permission: CreateFolderPermissionParams['permissionData']
  ) => void;
  onRemovePermission: (
    permission: CreateFolderPermissionParams['permissionData']
  ) => void;
  loading?: boolean;
}
/** Example component - use as template for creating components */
export const ProjectWideTab: React.FC<ProjectWideTabProps> = ({
  permissionList,
  loading,
  folder,
  onAddPermission,
  onRemovePermission,
}) => {
  const intl = useIntl();
  const style = useStyles(styleFn);
  const tabStyles = useStyles(tabStyleFn);

  const secGroupsQuery = useSecurityGroups({ enabled: false });
  const isSuperuser = useIsSuperuser();

  const foldersQuery = useFolders(
    {
      as_admin: isSuperuser ? true : undefined,
      show_deleted: false,
    },
    { enabled: false }
  );

  const defaultExpandedKeys = useRef(
    folder
      ? [
          ...getParentsIds(foldersQuery.data || [], String(folder.id)),
          String(folder.id),
        ]
      : []
  );

  const secGroupsOptions = useMemo(
    () =>
      mapSecGroupsToSections(secGroupsQuery.data).map((section) => ({
        ...section,
        // translate section label
        label:
          intl.formatMessage({
            id: section.intlId,
            defaultMessage: section.label,
          }) || section.label,
      })),
    [secGroupsQuery.data, intl]
  );

  const tableData = useMemo(() => arrToTree(foldersQuery.data || [], null), [
    foldersQuery.data,
  ]);

  const customReadTitle = () => {
    return (
      <div>
        {intl.formatMessage({ id: 'read' })}
        <IconButton
          icon={['far', 'info-circle']}
          customStyle={style.icons}
          containerStyle={{ marginRight: 5 }}
          backgroundOff
          tooltip={intl.formatMessage({ id: 'read-rights-info' })}
        />
      </div>
    );
  };

  const customUploadTitle = () => {
    return (
      <div>
        {intl.formatMessage({ id: 'upload' })}
        <IconButton
          icon={['far', 'info-circle']}
          customStyle={style.icons}
          containerStyle={{ marginRight: 5 }}
          backgroundOff
          tooltip={intl.formatMessage({ id: 'upload-rights-info' })}
        />
      </div>
    );
  };

  const customCreateAccessTitle = () => {
    return (
      <div>
        {intl.formatMessage({ id: 'create' })}
        <IconButton
          icon={['far', 'info-circle']}
          customStyle={style.icons}
          containerStyle={{ marginRight: 5 }}
          backgroundOff
          tooltip={intl.formatMessage({ id: 'create-rights-info' })}
        />
      </div>
    );
  };

  const customManageAccessTitle = () => {
    return (
      <div>
        {intl.formatMessage({ id: 'manage' })}
        <IconButton
          icon={['far', 'info-circle']}
          customStyle={style.icons}
          containerStyle={{ marginRight: 5 }}
          backgroundOff
          tooltip={intl.formatMessage({ id: 'manage-rights-info' })}
        />
      </div>
    );
  };

  /** Filters security groups for specific row(folder) & column(access level) */
  const getSecurityGroupsForColumn = (
    folderId: string | null,
    level: RIGHTS_LEVEL
  ) => {
    return (
      permissionList
        // get entry for folder
        .find((entry) => entry.folderId === folderId)
        // filter matching READ
        ?.permissions.filter((perm) => perm.level === level)
        // map security groups
        .map((perm) =>
          secGroupsQuery.data?.find(
            (secGroup) => secGroup.id === perm.security_group_id
          )
        )
        .filter(Boolean) as DbSecurityGroup[] | undefined
    );
  };

  const columns: TableProps<TableEntry>['columns'] = [
    {
      key: 'folderStructure',
      title: intl.formatMessage({ id: 'folder-structure' }),
      dataIndex: 'title',
      width: 280,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          color:
            !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
              ? 'rgba(0,0,0,0.25)'
              : undefined,
        },
      }),
      render: (title, record) => {
        const isTenderFolder = record.dbFolder?.option?.is_tender_folder;
        return {
          children: isTenderFolder ? (
            <Fragment>
              <NavIcon
                customStyle={{ width: '24px', marginRight: '6px' }}
                icon={getTenderIconArichive(record.dbFolder?.option)}
              />
              {title}
            </Fragment>
          ) : (
            <Fragment>
              <IconButton
                icon={['fas', 'folder']}
                customStyle={tabStyles.folderIcon}
                containerStyle={tabStyles.folderIconContainer}
                backgroundOff
              />
              {title}
            </Fragment>
          ),
        };
      },
    },
    {
      key: 'read',
      title: customReadTitle,
      onCell: () => ({ style: { padding: 4 } }),
      render: (x, record) =>
        secGroupsQuery.data ? (
          <SecurityGroupsColumn
            rightsLevel={RIGHTS_LEVEL.OPEN}
            options={secGroupsOptions}
            selectedGroups={getSecurityGroupsForColumn(
              record.key,
              RIGHTS_LEVEL.OPEN
            )}
            folderId={+record.key}
            disabled={
              !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
            }
            onAddPermission={onAddPermission}
            onRemovePermission={onRemovePermission}
          />
        ) : null,
    },
    {
      key: 'upload',
      title: customUploadTitle,
      onCell: () => ({ style: { padding: 4 } }),
      render: (x, record) =>
        secGroupsQuery.data ? (
          <SecurityGroupsColumn
            rightsLevel={RIGHTS_LEVEL.UPLOAD}
            options={secGroupsOptions}
            selectedGroups={getSecurityGroupsForColumn(
              record.key,
              RIGHTS_LEVEL.UPLOAD
            )}
            folderId={+record.key}
            disabled={
              !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
            }
            onAddPermission={onAddPermission}
            onRemovePermission={onRemovePermission}
          />
        ) : null,
    },
    {
      key: 'create',
      title: customCreateAccessTitle,
      onCell: () => ({ style: { padding: 4 } }),
      render: (x, record) =>
        secGroupsQuery.data ? (
          <SecurityGroupsColumn
            rightsLevel={RIGHTS_LEVEL.WRITE}
            options={secGroupsOptions}
            selectedGroups={getSecurityGroupsForColumn(
              record.key,
              RIGHTS_LEVEL.WRITE
            )}
            folderId={+record.key}
            disabled={
              !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
            }
            onAddPermission={onAddPermission}
            onRemovePermission={onRemovePermission}
          />
        ) : null,
    },
    {
      key: 'manage',
      title: customManageAccessTitle,
      onCell: () => ({ style: { padding: 4 } }),
      render: (x, record) =>
        secGroupsQuery.data ? (
          <SecurityGroupsColumn
            rightsLevel={RIGHTS_LEVEL.MANAGE}
            options={secGroupsOptions}
            selectedGroups={getSecurityGroupsForColumn(
              record.key,
              RIGHTS_LEVEL.MANAGE
            )}
            folderId={+record.key}
            disabled={
              !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
            }
            onAddPermission={onAddPermission}
            onRemovePermission={onRemovePermission}
          />
        ) : null,
    },
  ];

  return (
    <div css={style.container}>
      <Table<TableEntry>
        showSorterTooltip={false}
        bordered
        pagination={false}
        scroll={{ y: '50vh', x: 1090 }}
        expandable={{ defaultExpandedRowKeys: defaultExpandedKeys.current }}
        loading={loading}
        columns={columns}
        dataSource={tableData}
        rowClassName={(record) =>
          folder && record.key === String(folder.id)
            ? 'ant-table-row-selected'
            : ''
        }
      />
    </div>
  );
};

/** Visible tags by default */
const MAX_TAG_COUNT = 3;
interface SecurityGroupsColumnProps extends Partial<SelectProps<any>> {
  options: SelectProps<any>['options'];
  selectedGroups?: DbSecurityGroup[];
  folderId: number;
  rightsLevel: RIGHTS_LEVEL;
  onAddPermission: ProjectWideTabProps['onAddPermission'];
  onRemovePermission: ProjectWideTabProps['onRemovePermission'];
}
const SecurityGroupsColumn: React.FC<SecurityGroupsColumnProps> = ({
  options,
  selectedGroups = [],
  folderId,
  rightsLevel,
  onAddPermission,
  onRemovePermission,
  ...selectProps
}) => {
  const style = useStyles(styleFn);
  const [tagsVisible, setTagsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Select
      className="borderless"
      css={{ width: '100%' }}
      mode="tags"
      open={isOpen}
      onDropdownVisibleChange={(isItOpen) => {
        if (isOpen) {
          setIsOpen(isItOpen);
        }
      }}
      onClick={() => setIsOpen((prevState) => !prevState)}
      optionFilterProp="label"
      dropdownMatchSelectWidth={false}
      maxTagCount={tagsVisible ? undefined : MAX_TAG_COUNT}
      maxTagPlaceholder={(hidden) => `+${hidden.length}`}
      maxTagTextLength={5}
      options={options}
      value={selectedGroups.map((g) => g.id)}
      tagRender={({ value, label, closable, ...props }) => {
        /** if it's `+n` tag */
        const isTrailing = !value;
        const group = !isTrailing
          ? selectedGroups.find((v) => v.id === value)
          : undefined;
        const text = !isTrailing ? group?.name : label;

        let color: string | undefined;
        if (!isTrailing && group) {
          // `group.color` has priority
          if (group.color) {
            color = secGroupColorMap[group.color];
          } else {
            // use groups section color
            color =
              secGroupSectionsConfig[group.priority].color ||
              secGroupSectionsConfig[SEC_GROUP_PRIORITY.CUSTOM].color;
          }
        }

        return (
          <Tag
            css={[style.tag, isTrailing && { cursor: 'pointer' }]}
            color={color}
            closable={closable && !selectProps.disabled}
            onClick={isTrailing ? () => setTagsVisible(true) : undefined}
            {...props}>
            {text || '??'}
          </Tag>
        );
      }}
      onSelect={(value) => {
        onAddPermission({
          folder_id: folderId,
          level: rightsLevel,
          security_group_id: value,
        });
        setTagsVisible(false);
      }}
      onDeselect={(value) => {
        onRemovePermission({
          folder_id: folderId,
          level: rightsLevel,
          security_group_id: value,
        });
        setIsOpen(false);
      }}
      {...selectProps}
    />
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {},
  tag: {
    marginRight: 3,
    animation: `${fadeInKeyframes} 0.2s ease-out`,
  },
});
